import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useQuotationEditor } from "@/hooks/useQuotationEditor";
import { qgApi, errorMessage, sectionCode } from "@/lib/qgApi";
import { EditorHeader } from "@/components/quotation-generator/EditorHeader";
import { SectionNav } from "@/components/quotation-generator/SectionNav";
import { DetailsPanel } from "@/components/quotation-generator/DetailsPanel";
import { SectionPanel } from "@/components/quotation-generator/SectionPanel";
import { SummaryPanel } from "@/components/quotation-generator/SummaryPanel";

const scrollTop = () => document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });

export default function QuotationEditorPage() {
  const { quotationId } = useParams();
  const { q, calc, loadError, saveState, save, actions, replace, readOnly } = useQuotationEditor(quotationId);
  const [active, setActive] = useState("details");
  const [busy, setBusy] = useState(false);
  const [pdfRevision, setPdfRevision] = useState(null);

  const order = q ? ["details", ...q.sections.map((s) => s.key), "summary"] : [];
  const go = useCallback((key) => { setActive(key); scrollTop(); }, []);

  const jump = useCallback((err) => {
    setActive(err.section_key);
    const target = err.item_id ? `qg-item-${err.item_id}` : err.group_id ? `qg-group-${err.group_id}` : null;
    setTimeout(() => {
      const el = target ? document.getElementById(target) : null;
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-400"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 1800); }
      else if (err.section_key === "details" && err.field) document.querySelector(`[data-testid="qg-detail-${err.field}"]`)?.focus();
      else scrollTop();
    }, 80);
  }, []);

  useEffect(() => { if (saveState.status === "error" && saveState.error) toast.error(saveState.error); }, [saveState.status, saveState.error]);

  const saveNow = async () => { if (await save()) toast.success("Draft saved"); };
  const beforePdf = async () => { if (!readOnly && pdfRevision === null && !(await save())) throw new Error("Save failed"); };

  const finalize = async () => {
    if (!calc.canFinalize) {
      go("summary");
      toast.error(`${calc.errors.length} issue(s) must be resolved before generating the final quotation`);
      return;
    }
    setBusy(true);
    try {
      if (!(await save())) return;
      const doc = await qgApi.finalize(q.id);
      replace(doc);
      setPdfRevision(null);
      toast.success(`Final quotation ${doc.version_label} generated — PDF actions now use the issued revision`);
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      if (errs) { go("summary"); toast.error(`${errs.length} issue(s) must be resolved first`); } else toast.error(errorMessage(e, "Could not generate final quotation"));
    } finally { setBusy(false); }
  };

  const newRevision = async () => {
    setBusy(true);
    try { const doc = await qgApi.newRevision(q.id); replace(doc); toast.success(`Now editing ${doc.version_label}. ${q.version_label} stays issued and unchanged.`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  if (loadError) return <div className="p-8"><p className="text-red-600 mb-3" data-testid="qg-load-error">{loadError}</p><Link to="/quotation-generator" className="text-brillien-600 underline">Back to quotations</Link></div>;
  if (!q || !calc) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading quotation…</div>;

  const idx = order.indexOf(active);
  const labelOf = (k) => (k === "details" ? "Project details" : k === "summary" ? "Summary" : q.sections.find((s) => s.key === k)?.name);
  const nav = { onPrev: () => go(order[Math.max(0, idx - 1)]), onNext: () => go(order[Math.min(order.length - 1, idx + 1)]), prevLabel: labelOf(order[Math.max(0, idx - 1)]), nextLabel: labelOf(order[Math.min(order.length - 1, idx + 1)]) };
  const section = q.sections.find((s) => s.key === active);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="qg-editor-page">
      <EditorHeader q={q} readOnly={readOnly} saveState={saveState} onSave={saveNow} onFinalize={finalize} onNewRevision={newRevision} busy={busy}
        pdfRevision={pdfRevision} setPdfRevision={setPdfRevision} beforePdf={beforePdf} />
      {readOnly && (
        <div className="mx-6 mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-testid="qg-issued-banner">
          {q.version_label} is issued and locked. Its PDF will not change. Use “Edit as R{(q.revision_number || 0) + 1}” to make changes in a new revision.
        </div>
      )}
      <div className="flex gap-6 px-6 py-6">
        <aside className="w-64 shrink-0 hidden md:block">
          <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1"><SectionNav q={q} calc={calc} active={active} onSelect={go} /></div>
        </aside>
        <div className="flex-1 min-w-0 max-w-6xl">
          <div className="md:hidden mb-4">
            <select value={active} onChange={(e) => go(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="qg-mobile-nav">
              {order.map((k) => <option key={k} value={k}>{k in calc.categories ? `${sectionCode(q.sections, k)} · ` : ""}{labelOf(k)}</option>)}
            </select>
          </div>
          {active === "details" && <DetailsPanel q={q} calc={calc} readOnly={readOnly} actions={actions} onNext={nav.onNext} />}
          {section && (
            <SectionPanel key={section.key} section={section} code={sectionCode(q.sections, section.key)} catCalc={calc.categories[section.key]}
              readOnly={readOnly} actions={actions} {...nav} />
          )}
          {active === "summary" && <SummaryPanel q={q} calc={calc} readOnly={readOnly} actions={actions} onJump={jump} />}
        </div>
      </div>
    </div>
  );
}
